import React, { useState } from "react";
import MultiColumnLayout from "src/components/Shared/MultiColumnLayout/MultiColumnLayout";
import useGetListAndSelectItem from "src/hooks/useGetListAndSelectItem";
import Search from "src/components/Shared/Search";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import CustomAppItem from "./CustomAppItem";
import CustomAppBodyContent from "./BodyContent/CustomAppBodyContent";
import Modal from "src/components/Shared/Modal";
import { apiRequest } from "src/async/apiUtils";
import { toast } from "react-toastify";
import {
  customAppStatuses,
  customAppTypes,
} from "src/helpers/customAppHelper";
import { ArrowPathIcon, TrashIcon } from "@heroicons/react/24/outline";
import Button from "src/components/Shared/Buttons/Button";

const CustomAppsList = () => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    lists,
    setLists,
    meta,
    isLoaded,
    limit,
    offset,
    setOffset,
    keyword,
    setKeyword,
    selectedItem,
    setSelectedItem,
    handleOnPaginationChange,
    refreshData,
  } = useGetListAndSelectItem({
    url: `/custom-apps`,
    paginationData: { limit: 10, offset: 0 },
    additionalQuery: {
      status: statusFilter,
      appType: typeFilter,
    },
  });

  const handleOnDeleteModalOpen = () => setDeleteModalIsOpen(true);

  const handleOnDelete = async () => {
    try {
      setIsDeleting(true);
      const { data } = await apiRequest("DELETE", `/custom-apps/:id`, {
        params: { id: selectedItem?._id },
      });
      setDeleteModalIsOpen(false);
      refreshData();
      toast.success(data?.message || "Custom app successfully deleted");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const headerActions = [
    {
      title: "Delete App",
      icon: TrashIcon,
      onClick: handleOnDeleteModalOpen,
    },
  ];

  return (
    <>
      <MultiColumnLayout
        title="Custom Apps"
        subTitle={`Total of ${meta?.count || 0} custom apps`}
        asideLoaded={isLoaded}
        aside={lists}
        paginationEnabled={true}
        paginationData={{ limit, offset, meta }}
        handleOnPaginationChange={handleOnPaginationChange}
        itemEntry={(item) => <CustomAppItem data={item} />}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        mainHeaderContent={
          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="relative w-40">
              <SelectMenu
                inline
                label="Status"
                startIndex={Math.max(
                  customAppStatuses.findIndex((s) => s.value === statusFilter),
                  0
                )}
                options={customAppStatuses}
                setOption={(option) => {
                  setStatusFilter(option.value);
                  setOffset(0);
                }}
              />
            </div>

            {/* App Type Filter */}
            <div className="relative w-36">
              <SelectMenu
                inline
                label="Type"
                startIndex={Math.max(
                  customAppTypes.findIndex((t) => t.value === typeFilter),
                  0
                )}
                options={customAppTypes}
                setOption={(option) => {
                  setTypeFilter(option.value);
                  setOffset(0);
                }}
              />
            </div>

            {/* Search Box */}
            <div className="w-72">
              <Search
                keyword={keyword}
                setKeyword={(val) => {
                  setOffset(0);
                  setKeyword(val);
                }}
                placeholder="Search custom apps..."
                inline={true}
              />
            </div>

            {/* Refresh Button */}
            <Button
              type="button"
              version="default"
              onClick={() => refreshData()}
              className="flex !h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 hover:border-gray-300 transition-all"
              title="Refresh list"
            >
              <ArrowPathIcon className="h-4 w-4 text-gray-500" />
              <span>Refresh</span>
            </Button>
          </div>
        }
        bodyHeaderActions={headerActions}
        bodyContent={<CustomAppBodyContent data={selectedItem} />}
      />

      <Modal
        title="Custom App"
        secondaryTitle="Delete"
        isOpen={deleteModalIsOpen}
        onCancel={() => setDeleteModalIsOpen(false)}
        isLoading={isDeleting}
        onSuccess={handleOnDelete}
        defaultOptions={{
          onSuccessButtonText: "Delete",
        }}
      >
        <div className="flex flex-col gap-y-1 whitespace-nowrap px-3 py-6 text-gray-600">
          <p className="text-lg">
            Are you sure you want to{" "}
            <span className="px-1 font-semibold text-gray-700">DELETE</span>
            this custom app?
          </p>
          <div className="text-gray-400">
            {selectedItem?.name ? `App: ${selectedItem.name}. ` : ""}Once deleted, it cannot be undone.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CustomAppsList;
